import { inject, onUnmounted, shallowRef, type InjectionKey } from 'vue';

/**
 * Polymorphic record binding for an agent conversation.
 * Lives on the chat thread only — never as a type column on business rows.
 * Shape mirrors master mentions: module + resource_type + resource_id.
 */
export interface AgentRecordContext {
  /** Producing shell/module key, e.g. "erp", "drive", "mail". */
  moduleKey: string;
  /** Module-owned type vocabulary, e.g. "sales_quote", "customer". */
  resourceType: string;
  /** ID in the producing module (string for UUID/int parity). */
  resourceId: string;
  /** Optional human label for sidebar / prompt. */
  label?: string;
}

/**
 * Result of a FAB "Assistent" claim. Context is optional; claimants may
 * also run a side effect (e.g. open a local drawer) via onOpen.
 */
export interface AssistantClaimResult {
  context?: AgentRecordContext | null;
  onOpen?: () => void;
}

export type AssistantClaimHandler = () => AssistantClaimResult | void;

/**
 * Shell → module channel for the FAB's "Assistent" action.
 * Connect provides it; any mounted surface may claim it (B-DMS today,
 * other contexts later). Context is optional — without a claim (or with
 * an empty claim) the shell opens the agent app bare.
 */
export interface AssistantChannel {
  /** Register a claim handler; returns a release function. Last claim wins. */
  claim(handler: AssistantClaimHandler): () => void;
  /**
   * Invoke the latest claimant.
   * - null  → no surface claimed
   * - result → optional context + optional onOpen (always invoke onOpen if set)
   */
  request(): AssistantClaimResult | null;
}

export const assistantChannelKey: InjectionKey<AssistantChannel> = Symbol('assistantChannel');

export function createAssistantChannel(): AssistantChannel {
  const handlers = shallowRef<AssistantClaimHandler[]>([]);
  return {
    claim(handler) {
      handlers.value = [...handlers.value, handler];
      return () => {
        handlers.value = handlers.value.filter((h) => h !== handler);
      };
    },
    request() {
      const handler = handlers.value.at(-1);
      if (!handler) return null;
      const result = handler();
      if (result == null) return {};
      return result;
    },
  };
}

/** Consumer side: claim for this component's lifetime. No-op when no ancestor provides the channel. */
export function useAssistantTarget(handler: AssistantClaimHandler): void {
  const channel = inject(assistantChannelKey, undefined);
  if (!channel) return;
  const release = channel.claim(handler);
  onUnmounted(release);
}

/** Build Connect agent route query from a record binding. */
export function agentContextQuery(ctx: AgentRecordContext): Record<string, string> {
  const q: Record<string, string> = {
    module: ctx.moduleKey,
    resourceType: ctx.resourceType,
    resourceId: ctx.resourceId,
  };
  if (ctx.label?.trim()) q.label = ctx.label.trim();
  return q;
}
